import * as React from "react";
import styled from "styled-components";

const RADIO_RED = "#e11d48";

const PhysicianNumberRadioGroupRoot = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 16px;
`;

const PhysicianNumberRadioOption = styled<{ disabled?: boolean }, "label">("label")`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: ${p => (p.disabled ? "not-allowed" : "pointer")};
  font-size: 14px;
  position: relative;

  /* hide default radio but keep it accessible */
  input[type="radio"] {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  /* custom circle */
  .radio-visual {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    box-sizing: border-box;
    border: 2px solid ${RADIO_RED};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: box-shadow .15s ease;
  }

  /* inner red dot when checked */
  input[type="radio"]:checked + .radio-visual::after {
    content: "";
    width: 10px; height: 10px; border-radius: 50%;
    background: ${RADIO_RED};
  }

  /* focus ring */
  input[type="radio"]:focus-visible + .radio-visual {
    box-shadow: 0 0 0 3px rgba(225, 29, 72, 0.35);
  }

  .option-label {
    display: flex;
    font-weight: normal;
    gap: 16px;
    width: 100%;
  }

  .physician-name-option { 
    width: 240px;
  }
  .account-number-option { 
    width: 200px;
  }
  .address-notes-option {
    flex: 1;
  }
`;

export default function PhysicianNumberRadioGroup<T extends string | number>({
  name,
  options,
  value,
  onChange,
  disabled,
}: {
  name: string;
  options: { id: number, physicianName: string, accountNumber: string; addressNotes: string }[];
  value: T | null | undefined;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  return (
    <PhysicianNumberRadioGroupRoot role="radiogroup" aria-labelledby={`${name}-label`}>
      {options.map(opt => (
        <PhysicianNumberRadioOption key={String(opt.accountNumber)} disabled={disabled}>
          <input
            type="radio"
            name={name}
            value={String(opt.accountNumber)}
            checked={value === opt.id}
            onChange={() => onChange(opt.id)}
            disabled={disabled}
          />
          <span className="radio-visual" />
          <span className="option-label">
            <span className="physician-name-option">{opt.physicianName}</span>
            <span className="account-number-option">{opt.accountNumber}</span>
            {opt.addressNotes && <span className="address-notes-option">{opt.addressNotes}</span>}
          </span>
        </PhysicianNumberRadioOption>
      ))}
    </PhysicianNumberRadioGroupRoot>
  );
}
